<template>
  <div class="discount-code-root">
    <!-- Promo tooltip (post-exit-intent callout). Shown per `tooltipRules`
         (json-logic visibility rules; no rule → visible). Rendered OUTSIDE the
         masked card so it can pop above the card's top-left corner without being
         clipped by the card mask. The text is fully author-written rich text. -->
    <div v-if="showTooltip" :ref="setTooltipEl" class="discount-code__tooltip">
      <div v-if="fields.tooltipContent?.key" class="discount-code__tooltip-content">
        <TipTap v-model="tooltipContentValue" />
      </div>
      <!-- Downward-pointing corner. Lifted verbatim from the legacy
           `pipe-promo-code-corner` SVG; `fill` is driven from the tooltip
           background color. -->
      <div class="discount-code__tooltip-corner">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="8"
          fill="none"
          aria-hidden="true"
        >
          <path d="M32 0 16 8 0 0h32Z" />
        </svg>
      </div>
    </div>

    <div ref="cardEl" class="discount-code">
      <!-- Top row. When the tooltip shows, the headline is nudged clear of the
           tooltip column (measured inset); otherwise it stays centred. -->
      <div class="discount-code__top" :class="{ 'discount-code__top--with-tooltip': showTooltip }">
        <div class="discount-code__headline">
          <svg
            class="discount-code__icon"
            viewBox="57 21 22 22"
            width="24"
            height="24"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                :id="`${uid}-tag`"
                x1="73.641"
                y1="37.6409"
                x2="60.141"
                y2="24.141"
                gradientUnits="userSpaceOnUse"
              >
                <stop class="discount-code__tag-stop-start" offset="0" />
                <stop class="discount-code__tag-stop-end" offset="1" />
              </linearGradient>
            </defs>
            <!-- White background plate so the card gradient doesn't bleed
                 through the cut-out. Taken verbatim from the SVG source. -->
            <path :d="TAG_PLATE_D" fill="white" />
            <path :d="TAG_SHAPE_D" :fill="`url(#${uid}-tag)`" />
          </svg>
          <!-- Headline slot: authors drop one or more components here, each with
               its own node-level visibility rules. -->
          <div class="discount-code__headline-slot">
            <RenderSlot name="headline" :children="slotChildren?.headline" :editable="editable" />
          </div>
        </div>
      </div>

      <div ref="dividerEl" class="discount-code__divider" />

      <div class="discount-code__bottom">
        <div class="discount-code__code-box">
          <svg
            class="discount-code__check"
            viewBox="0 0 16 16"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              d="M3 8.5 L6.5 12 L13 4.5"
              fill="none"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="discount-code__code-text">{{ codeText }}</span>
        </div>

        <!-- Timer as a 3-column grid: the MM column and SS column are `1fr`
           each so their widths track each other exactly, which keeps the
           colon (middle `auto` column) locked to the horizontal centre of
           the timer regardless of MM/SS label lengths. Digits/labels
           centre inside their own column via `justify-items: center`.
           Direction (`timerDir`) is 'ltr' by default so the timer always reads
           MM:SS left-to-right — under a page-level dir="rtl" the grid columns
           would otherwise reverse and swap the minutes/seconds order. Authors
           opt into mirroring per-instance via `flipOnRtl`. Matches the
           numeric-widget precedent (SteppedSlider, SliderInput, BMIVisual). -->
        <div class="discount-code__timer" :dir="timerDir">
          <span class="discount-code__timer-digit discount-code__timer-digit--mm">{{ mm }}</span>
          <!-- Colon lifted from discountapplied.svg (path 10): two stacked
             dots in the CF Green Secondary 750 (#099F96). `fill: currentColor`
             picks up the digit color so it stays visually coherent. -->
          <svg
            class="discount-code__timer-colon"
            viewBox="287.793 92.048 4.416 12.096"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <path :d="COLON_D" fill="currentColor" />
          </svg>
          <span class="discount-code__timer-digit discount-code__timer-digit--ss">{{ ss }}</span>
          <div
            v-if="fields.minutesLabel?.key"
            class="discount-code__timer-label discount-code__timer-label--mm"
          >
            <TipTap v-model="minutesValue" mode="single" />
          </div>
          <div
            v-if="fields.secondsLabel?.key"
            class="discount-code__timer-label discount-code__timer-label--ss"
          >
            <TipTap v-model="secondsValue" mode="single" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'

import type { ComponentNode } from '@api/types.gen'
import type { ColorField, SizeField } from '@render/types/styles'

import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { useTranslation } from '@render/composables/useTranslation'
import { useDirection } from '@render/composables/useDirection'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useSmartRule } from '@render/composables/useLogic'
import { usePipe } from '@render/composables/usePipe'
import RenderSlot from '@render/RenderSlot.vue'
import TipTap from '@render/lib/TipTap'
import type { DiscountCodeFields as Model } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

// Slot children (grouped by slot name) + editability, passed by RenderComponents
// because this component declares a `headline` slot. Rendered via <RenderSlot>.
defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

const pipe = usePipe()
const uid = useId()

// Timer direction. The timer always reads MM:SS left-to-right; under a
// page-level dir="rtl" the grid columns would otherwise reverse and swap the
// minutes/seconds order. So it stays 'ltr' unless the author opts into
// mirroring via `flipOnRtl` AND the active language is RTL — matching the
// numeric-widget precedent (SteppedSlider, SliderInput, BMIVisual).
const { dir } = useDirection()
const timerDir = computed<'rtl' | 'ltr'>(() => (fields.value.flipOnRtl ? dir.value : 'ltr'))

// Element refs — used to compute where the divider sits inside the card
// so the side cutouts line up with the horizontal rule (the divider's
// vertical position is dynamic: when the headline wraps, the top row
// grows and the divider moves down).
const cardEl = ref<HTMLDivElement | null>(null)
const dividerEl = ref<HTMLDivElement | null>(null)

// Divider's vertical centre in px, measured from the card's top edge.
// Seed with the static design value (148-tall SVG has the divider at
// y=64) so first paint is close to correct even before the observer
// measures live dimensions.
const dividerY = ref(64)
let dividerRO: ResizeObserver | null = null
let dividerRaf = 0

function updateDividerY() {
  const card = cardEl.value
  const div = dividerEl.value
  if (!card || !div) return
  // Use `offsetTop` / `offsetHeight` (integer layout pixels) rather than
  // `getBoundingClientRect()` (post-transform CSS pixels) so an ancestor
  // `transform: scale(...)` or CSS `zoom` on the preview host doesn't
  // make our Y disagree with the one the mask image consumes (which
  // uses the element's own layout units).
  //
  // `offsetTop` measures from the offsetParent's padding-box top. The
  // mask is `mask-origin: border-box`, so add the card's `borderTop` to
  // translate into border-box coords.
  const borderTopStr = getComputedStyle(card).borderTopWidth
  const borderTop = Number.parseFloat(borderTopStr) || 0
  dividerY.value = borderTop + div.offsetTop + div.offsetHeight / 2
}

// Defer the reactive write to avoid
// "ResizeObserver loop completed with undelivered notifications".
function scheduleUpdateDividerY() {
  if (dividerRaf) return
  dividerRaf = requestAnimationFrame(() => {
    dividerRaf = 0
    updateDividerY()
  })
}

// Expose dividerY as a px string for the CSS radial-gradient's `at 0 Y`
// position (v-bind can't do the px concat inline in the CSS string).
const dividerYPx = computed(() => `${dividerY.value}px`)

// Tag icon paths — lifted from discountapplied.svg. Coordinates match
// the original SVG so the viewBox (57 21 22 22) crops to the tag glyph
// with no translation math needed.
const TAG_PLATE_D =
  'M76.9 36.5091L72.509 40.9001C71.3327 42.0758 69.7376 42.7362 68.0745 42.7362C66.4114 42.7362 64.8163 42.0758 63.64 40.9001L59.105 36.3601C58.488 35.7417 58.0066 35.0016 57.6916 34.1868C57.3765 33.3721 57.2346 32.5007 57.275 31.6281L57.514 26.6281C57.5711 25.2909 58.1275 24.0238 59.0734 23.0769C60.0193 22.1301 61.2859 21.5724 62.623 21.5141L67.628 21.2751C67.728 21.2751 67.828 21.2691 67.928 21.2691C68.751 21.2666 69.5664 21.4272 70.3271 21.7415C71.0877 22.0557 71.7787 22.5175 72.36 23.1001L76.9 27.6401C78.0742 28.8172 78.7337 30.4119 78.7337 32.0746C78.7337 33.7372 78.0742 35.332 76.9 36.5091Z'
const TAG_SHAPE_D =
  'M75.834 28.7L71.3 24.166C70.8301 23.6959 70.267 23.3293 69.6471 23.0896C69.0271 22.8498 68.3639 22.7423 67.7 22.774L62.7 23.012C61.7361 23.0561 60.8235 23.4588 60.1412 24.1411C59.4589 24.8234 59.0561 25.736 59.012 26.6999L58.774 31.7C58.7424 32.3639 58.8499 33.0271 59.0896 33.647C59.3293 34.267 59.696 34.83 60.166 35.3L64.7 39.834C65.5947 40.7286 66.8082 41.2312 68.0735 41.2312C69.3388 41.2312 70.5523 40.7286 71.447 39.834L75.833 35.448C76.2762 35.005 76.6278 34.479 76.8678 33.9001C77.1077 33.3213 77.2312 32.7008 77.2313 32.0742C77.2314 31.4475 77.108 30.827 76.8683 30.2481C76.6285 29.6691 76.2771 29.1431 75.834 28.7ZM65.5 32.375C64.9314 32.375 64.3755 32.2063 63.9027 31.8904C63.43 31.5745 63.0615 31.1255 62.8439 30.6002C62.6263 30.0748 62.5693 29.4968 62.6803 28.9391C62.7912 28.3814 63.065 27.8691 63.4671 27.467C63.8692 27.0649 64.3814 26.7911 64.9391 26.6802C65.4968 26.5693 66.0749 26.6262 66.6002 26.8438C67.1256 27.0614 67.5746 27.4299 67.8905 27.9027C68.2064 28.3755 68.375 28.9313 68.375 29.5C68.374 30.2621 68.0707 30.9928 67.5318 31.5317C66.9928 32.0707 66.2622 32.3739 65.5 32.375Z'

// Colon — lifted from discountapplied.svg (path 10). Two stacked dots in
// the original design's #099F96 fill; we re-fill with currentColor so the
// colon re-uses the digit color from the `timerDigitColor` field.
const COLON_D =
  'M287.793 101.936C287.793 101.632 287.849 101.344 287.961 101.072C288.073 100.8 288.225 100.568 288.417 100.376C288.625 100.168 288.865 100.008 289.137 99.896C289.409 99.784 289.697 99.728 290.001 99.728C290.305 99.728 290.593 99.784 290.865 99.896C291.137 100.008 291.369 100.168 291.561 100.376C291.769 100.568 291.929 100.8 292.041 101.072C292.153 101.344 292.209 101.632 292.209 101.936C292.209 102.24 292.153 102.528 292.041 102.8C291.929 103.072 291.769 103.312 291.561 103.52C291.369 103.712 291.137 103.864 290.865 103.976C290.593 104.088 290.305 104.144 290.001 104.144C289.697 104.144 289.409 104.088 289.137 103.976C288.865 103.864 288.625 103.712 288.417 103.52C288.225 103.312 288.073 103.072 287.961 102.8C287.849 102.528 287.793 102.24 287.793 101.936ZM287.793 94.256C287.793 93.952 287.849 93.664 287.961 93.392C288.073 93.12 288.225 92.888 288.417 92.696C288.625 92.488 288.865 92.328 289.137 92.216C289.409 92.104 289.697 92.048 290.001 92.048C290.305 92.048 290.593 92.104 290.865 92.216C291.137 92.328 291.369 92.488 291.561 92.696C291.769 92.888 291.929 93.12 292.041 93.392C292.153 93.664 292.209 93.952 292.209 94.256C292.209 94.56 292.153 94.848 292.041 95.12C291.929 95.392 291.769 95.632 291.561 95.84C291.369 96.032 291.137 96.184 290.865 96.296C290.593 96.408 290.305 96.464 290.001 96.464C289.697 96.464 289.409 96.408 289.137 96.296C288.865 96.184 288.625 96.032 288.417 95.84C288.225 95.632 288.073 95.392 287.961 95.12C287.849 94.848 287.793 94.56 287.793 94.256Z'

// ---------- Translations ----------
const minutesValue = useTranslation(() => fields.value.minutesLabel?.key)
const secondsValue = useTranslation(() => fields.value.secondsLabel?.key)
const tooltipContentValue = useTranslation(() => fields.value.tooltipContent?.key)

const codeText = computed(
  () => (pipe.getData('var.global.discountCode') as string | null | undefined) ?? '',
)

// ---------- Visibility rules ----------
// The tooltip is gated by an optional json-logic rule (authored inline via the
// visibility-rules builder — same mechanism as Nutritionist Chat's per-bubble
// rules). `useSmartRule` re-evaluates reactively when the rule or any field it
// references changes. Semantics: no rule (or an empty rule) → visible; otherwise
// visible iff the rule evaluates true. (The headline is a slot, so its children
// carry their own node-level visibility rules — no field rule needed here.)
const getRuleData = (key: string) => pipe.getData(key)

function isEmptyRule(rule: unknown): boolean {
  return !rule || (typeof rule === 'object' && Object.keys(rule as object).length === 0)
}

const { result: tooltipRuleResult } = useSmartRule(
  computed(() => fields.value.tooltipRules),
  getRuleData,
)

// The tooltip pops above the card's top-left corner, so it's rendered OUTSIDE
// the masked `.discount-code` card (the mask would otherwise clip anything
// overflowing the card bounds) and the headline is nudged clear of the
// tooltip's width via a ResizeObserver measurement.
const showTooltip = computed(() =>
  isEmptyRule(fields.value.tooltipRules) ? true : tooltipRuleResult.value === true,
)

const tooltipWidth = ref(0)
let tooltipRO: ResizeObserver | null = null
let tooltipRaf = 0
// Callback ref: fires with the element on mount and `null` on unmount, so the
// measurement follows the toggle without a separate watcher.
function setTooltipEl(el: Element | { $el?: Element } | null) {
  tooltipRO?.disconnect()
  tooltipRO = null
  if (tooltipRaf) {
    cancelAnimationFrame(tooltipRaf)
    tooltipRaf = 0
  }
  const node = el instanceof HTMLElement ? el : null
  if (node && typeof ResizeObserver !== 'undefined') {
    tooltipRO = new ResizeObserver(() => {
      if (tooltipRaf) return
      tooltipRaf = requestAnimationFrame(() => {
        tooltipRaf = 0
        tooltipWidth.value = node.offsetWidth
      })
    })
    tooltipRO.observe(node)
    tooltipWidth.value = node.offsetWidth
  } else {
    tooltipWidth.value = 0
  }
}
// Inline-start clearance for the headline so it sits to the right of the
// tooltip column (matches the design; `0px` when the tooltip is hidden).
const headlineInsetPx = computed(() =>
  showTooltip.value && tooltipWidth.value ? `${tooltipWidth.value + 16}px` : '0px',
)

// ---------- Countdown ----------
// 2 minutes 59 seconds default — i.e. the timer immediately shows "02:59"
// and ticks down. Runs in EDITOR mode too, per spec, so authors see the
// animation without having to switch to preview.
const DEFAULT_SECONDS = 2 * 60 + 59
const startingSeconds = computed(() =>
  Math.max(1, Number(fields.value.countdownSeconds ?? DEFAULT_SECONDS)),
)
const remaining = ref(startingSeconds.value)

let tickId: ReturnType<typeof setInterval> | null = null
function startTimer() {
  if (tickId !== null) return
  tickId = setInterval(() => {
    remaining.value = remaining.value <= 1 ? startingSeconds.value : remaining.value - 1
  }, 1000)
}
function stopTimer() {
  if (tickId !== null) {
    clearInterval(tickId)
    tickId = null
  }
}
function resetTimer() {
  remaining.value = startingSeconds.value
}

onMounted(() => {
  startTimer()
  // Defer the first divider measurement to the next animation frame so
  // the DOM has fully laid out (avoids reading sizes before the card's
  // content has its final height — on first paint we'd otherwise cache
  // the seeded 64 px and never replace it until a real resize).
  requestAnimationFrame(() => {
    updateDividerY()
    if (cardEl.value && typeof ResizeObserver !== 'undefined') {
      dividerRO = new ResizeObserver(scheduleUpdateDividerY)
      dividerRO.observe(cardEl.value)
      if (dividerEl.value) dividerRO.observe(dividerEl.value)
    }
  })
})
onBeforeUnmount(() => {
  stopTimer()
  dividerRO?.disconnect()
  dividerRO = null
  if (dividerRaf) cancelAnimationFrame(dividerRaf)
  dividerRaf = 0
  tooltipRO?.disconnect()
  tooltipRO = null
  if (tooltipRaf) cancelAnimationFrame(tooltipRaf)
  tooltipRaf = 0
})

// If the author edits the countdown duration, snap remaining to the new
// starting value so it doesn't appear stuck at a stale number.
watch(startingSeconds, (v) => {
  remaining.value = v
})

useComponentProcessors({
  resetTimer: () => resetTimer(),
})

const mm = computed(() => String(Math.floor(remaining.value / 60)).padStart(2, '0'))
const ss = computed(() => String(remaining.value % 60).padStart(2, '0'))

// ---------- Styling ----------
function col(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

const css = computed(() => {
  const bgStart = col(fields.value.bgStartColor, '#BCEFE8')
  const bgMid = col(fields.value.bgMidColor, '#FFDB99')
  const bgEnd = col(fields.value.bgEndColor, '#AEDFF4')
  const borderStart = col(fields.value.borderStartColor, '#03CEA4')
  const borderMid = col(fields.value.borderMidColor, '#F1AF63')
  const borderEnd = col(fields.value.borderEndColor, '#03CEA4')
  const dividerStart = col(fields.value.dividerStartColor, '#03CEA4')
  const dividerMid = col(fields.value.dividerMidColor, '#F1AF63')
  const dividerEnd = col(fields.value.dividerEndColor, '#1A8CBD')
  return {
    width: sz(fields.value.width, '100%'),
    padding: getSpacingStyleData(fields.value.padding, '12px'),
    margin: getSpacingStyleData(fields.value.margin, '0'),
    borderWidth: sz(fields.value.borderWidth, '3px'),
    borderRadius: sz(fields.value.borderRadius, '12px'),
    codeBoxBg: col(fields.value.codeBoxBgColor, '#FFFFFF'),
    codeBoxBorderColor: col(fields.value.codeBoxBorderColor, '#03CEA4'),
    codeTextColor: col(fields.value.codeTextColor, '#0B3E53'),
    codeTextFontSize: sz(fields.value.codeTextFontSize, '16px'),
    checkColor: col(fields.value.checkColor, '#03CEA4'),
    timerBg: col(fields.value.timerBgColor, 'rgba(255, 255, 255, 0.4)'),
    timerDigitColor: col(fields.value.timerDigitColor, '#099F96'),
    timerDigitFontSize: sz(fields.value.timerDigitFontSize, '18px'),
    timerLabelColor: col(fields.value.timerLabelColor, '#0B3E53'),
    timerLabelFontSize: sz(fields.value.timerLabelFontSize, '14px'),
    tagIconColorStart: col(fields.value.tagIconColorStart, '#03CEA4'),
    tagIconColorEnd: col(fields.value.tagIconColorEnd, '#BDE5DF'),
    tooltipBg: col(fields.value.tooltipBgColor, '#099F96'),
    tooltipTextColor: col(fields.value.tooltipTextColor, '#FFFFFF'),
    // 3-stop diagonal matching SVG `paint0_linear_5215_17727`.
    bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 71%, ${bgEnd} 100%)`,
    // Vertical 3-stop border gradient (simplified from the SVG's 4-stop paint1).
    borderGradient: `linear-gradient(180deg, ${borderStart} 0%, ${borderMid} 50%, ${borderEnd} 100%)`,
    // Horizontal 3-stop divider matching SVG `paint3_linear_5215_17727`.
    dividerGradient: `linear-gradient(90deg, ${dividerStart} 0%, ${dividerMid} 50%, ${dividerEnd} 100%)`,
  }
})
</script>

<style scoped lang="scss">
// Non-clipped wrapper. Owns the component's outer sizing/margin and is the
// positioning context for the promo tooltip, which pops above the card and so
// must live outside the card's `overflow: hidden` + mask.
.discount-code-root {
  position: relative;
  width: v-bind('css.width');
  min-width: 260px;
  margin: v-bind('css.margin');
  box-sizing: border-box;
}

.discount-code {
  position: relative;
  width: 100%;
  // Min height matches the SVG design (148px). The component grows
  // vertically when the headline wraps — no max-height clamp.
  min-height: 148px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  // Gradient border + gradient fill via double-background with different
  // `background-clip` origins, matching the technique from Scratchoff.vue.
  background:
    v-bind('css.bgGradient') padding-box,
    v-bind('css.borderGradient') border-box;
  border: v-bind('css.borderWidth') solid transparent;
  border-radius: v-bind('css.borderRadius');
  overflow: hidden;

  // Transparent semicircle cutouts on the left and right edges, aligned
  // with the centre of the divider — mirrors the ticket-stub shape in
  // discountapplied.svg (paths 4 & 5: r=12 circles centred at x=0 and
  // x=cardWidth, y=dividerCentre). Two radial-gradient mask layers each
  // carve out one circle; `mask-composite: intersect` keeps only the
  // area that's black in BOTH masks.
  //
  // Gated behind `@supports (mask-composite: intersect)`: the standard
  // property only landed in Safari/iOS 15.4+ (and Chrome 120+, Firefox).
  // On iOS < 15.4 only the `-webkit-mask-composite: source-in` path was
  // available, and compositing these two radial layers down that path
  // collapses to an EMPTY mask — which hid the entire card. The notches
  // are purely cosmetic, so the safe degradation there is "no cutouts,
  // fully visible card" rather than an invisible component.
  @supports (mask-composite: intersect) {
    mask-image:
      radial-gradient(circle 12px at 0 v-bind(dividerYPx), transparent 11.5px, #000 12px),
      radial-gradient(circle 12px at 100% v-bind(dividerYPx), transparent 11.5px, #000 12px);
    mask-repeat: no-repeat, no-repeat;
    mask-origin: border-box, border-box;
    mask-clip: border-box, border-box;
    mask-position:
      0 0,
      0 0;
    mask-size:
      100% 100%,
      100% 100%;
    mask-mode: alpha, alpha;
    mask-composite: intersect;
  }
}

.discount-code__top {
  display: flex;
  // Icon vertically centered with the text block.
  align-items: center;
  justify-content: center;
  padding: v-bind('css.padding');
  flex: 1 1 auto;
  min-height: 0;
}

.discount-code__top--with-tooltip {
  justify-content: flex-start;
}

// Icon + headline text kept together as a unit. When the tooltip shows, the
// group is nudged right of the tooltip column by its measured width.
.discount-code__headline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  margin-inline-start: v-bind(headlineInsetPx);
}

.discount-code__icon {
  flex: 0 0 auto;
  display: block;
}

.discount-code__tag-stop-start {
  stop-color: v-bind('css.tagIconColorStart');
}
.discount-code__tag-stop-end {
  stop-color: v-bind('css.tagIconColorEnd');
}

// Headline slot wrapper. `flex: 0 1 auto` + `min-width: 0` lets the dropped
// content size to itself and wrap when space is tight, while the icon+slot
// block stays centred via the parent's `justify-content`.
.discount-code__headline-slot {
  flex: 0 1 auto;
  min-width: 0;
  text-align: center;
}

.discount-code__divider {
  height: 2px;
  margin: 0;
  background: v-bind('css.dividerGradient');
  flex: 0 0 auto;
}

.discount-code__bottom {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: v-bind('css.padding');
  flex: 0 0 auto;
}

.discount-code__code-box {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 56px;
  background-color: v-bind('css.codeBoxBg');
  border: 1px solid v-bind('css.codeBoxBorderColor');
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
}

// Promo tooltip — a callout popping above the card's top-left corner, with a
// downward corner pointing into the card. Absolutely positioned against the
// non-clipped `.discount-code-root` (see the wrapper note) so it can overflow
// the masked card. Mirrors the legacy `pipe-promo-code-tooltip` look.
.discount-code__tooltip {
  position: absolute;
  z-index: 3;
  top: -10px;
  inset-inline-start: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 8px;
  border-radius: 6px;
  background: v-bind('css.tooltipBg');
  color: v-bind('css.tooltipTextColor');
  text-align: center;
}

.discount-code__tooltip-content {
  color: inherit;
  white-space: nowrap;
  min-height: 30px;

  :deep(p) {
    margin: 0;
  }
}

.discount-code__tooltip-corner {
  position: absolute;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%);
  line-height: 0;

  svg path {
    fill: v-bind('css.tooltipBg');
  }
}

.discount-code__check {
  flex: 0 0 auto;
  stroke: v-bind('css.checkColor');
  display: block;
}

.discount-code__code-text {
  flex: 1 1 auto;
  min-width: 0;
  color: v-bind('css.codeTextColor');
  // "Acumin Pro" from the design spec with sans-serif fallback for
  // environments that don't have the font loaded.
  font-family: 'Acumin Pro', sans-serif;
  font-size: v-bind('css.codeTextFontSize');
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
  // Truncate with ellipsis if the computed code string is longer than
  // the horizontal space (e.g. long first names).
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Grid layout: `1fr auto 1fr` makes the MM and SS columns size-equal no
// matter what the label widths are, so the colon in the middle `auto`
// column stays locked to the timer's horizontal centre even when one
// label is longer than the other. Both digits and labels are
// `justify-items: center`, so each stacks in the middle of its own
// column — i.e. centred on its side of the colon.
.discount-code__timer {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 8px;
  row-gap: 2px;
  justify-items: center;
  align-items: center;
  padding: 6px 10px;
  min-height: 56px;
  min-width: 140px;
  background-color: v-bind('css.timerBg');
  border-radius: 6px;
  box-sizing: border-box;
}

.discount-code__timer-digit {
  color: v-bind('css.timerDigitColor');
  font-family: Avenir, sans-serif;
  font-size: v-bind('css.timerDigitFontSize');
  font-style: normal;
  font-weight: 900;
  line-height: 26px;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.discount-code__timer-digit--mm {
  grid-column: 1;
  grid-row: 1;
}
.discount-code__timer-digit--ss {
  grid-column: 3;
  grid-row: 1;
}

.discount-code__timer-colon {
  grid-column: 2;
  // Span both rows so the column's full height is reserved for the
  // colon, and centre the glyph vertically so it sits on the digit-row
  // midline (where the SVG design places it).
  grid-row: 1 / span 2;
  display: block;
  // Height tied to the digit font-size so the colon scales with the
  // digit size field. The viewBox aspect (4.416:12.096) drives the
  // rendered width.
  height: v-bind('css.timerDigitFontSize');
  // Derive the width from the viewBox aspect ratio (4.416 / 12.096 ≈ 0.365)
  // rather than `width: auto`. iOS < 16 Safari does NOT auto-size an inline
  // SVG that has a viewBox but no width/height attributes — it falls back to
  // the default ~300px replaced-element width, which blew the timer grid out
  // and forced a horizontal scroll. An explicit calc() width is exact in
  // every engine and scales with the digit font size.
  width: calc(v-bind('css.timerDigitFontSize') * 0.365);
  color: v-bind('css.timerDigitColor');
  align-self: start;
  // Nudge down so the two dots sit roughly on the digit baseline
  // rather than the top of the cell (matches the SVG composition).
  margin-top: 4px;
}

.discount-code__timer-label {
  color: v-bind('css.timerLabelColor');
  font-family: Avenir, sans-serif;
  font-size: v-bind('css.timerLabelFontSize');
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
}

.discount-code__timer-label--mm {
  grid-column: 1;
  grid-row: 2;
}
.discount-code__timer-label--ss {
  grid-column: 3;
  grid-row: 2;
}
</style>
